import type { RegistrationUnavailableReason } from "./types";

/**
 * Why a new registration cannot be started. The submit path rejects one with 409 for each of
 * these, so the kiosk says so up front rather than at Confirm.
 *
 * Speaker-only reasons return null — those close the Pick step, not registration itself.
 */
export function registrationClosedMessage(
  reason: RegistrationUnavailableReason | undefined
): string | null {
  switch (reason) {
    case "MeetingEnded":
      return "This meeting has ended, so registration is closed.";
    case "RegistrationNotYetOpen":
      return "Registration for this meeting hasn't opened yet.";
    case "RegistrationClosedBeforeMeeting":
      return "Registration for this meeting has closed.";
    case "RegistrationDisabled":
      return "Registration isn't available for this meeting.";
    case "AttendeeLimitReached":
      return "This meeting has reached its in-person registration limit.";
    default:
      return null;
  }
}

/** Label of the Welcome check-in button. The closed-registration copy quotes it by name. */
export const CHECK_IN_BUTTON_LABEL = "I'm registered, check in";

/**
 * What the resident should do next, shown after the closed-registration message.
 *
 * Not-yet-open cannot have prior registrations through this meeting's flow, so it never
 * says "already registered". The check-in button sits beside the text, not under it, so the
 * copy names the button rather than saying "below".
 */
export function registrationClosedFollowUp(
  reason: RegistrationUnavailableReason | undefined,
  participationCheckInEnabled: boolean
): string {
  if (!participationCheckInEnabled || reason === "RegistrationNotYetOpen") {
    return "Please see the meeting clerk for help.";
  }
  return `If you have already registered, use the "${CHECK_IN_BUTTON_LABEL}" button.`;
}

/** True when the payload says a new registration would be rejected. */
export function isRegistrationClosed(
  meeting:
    | {
        registrationOpen?: boolean;
      }
    | null
    | undefined
): boolean {
  return meeting?.registrationOpen === false;
}
